import React, { useState, useEffect } from 'react';
import HandshakeIcon from '../../../components/HandshakeIcon';
import {
  subscribe,
  getESGCommunityEngagementOverview,
  getPrograms,
  getStakeholderGroups,
  getImpactMetrics,
  getGrievances,
  getBenefitPrograms,
  getKPIMonitoring,
  getComplianceFramework,
  getProgramById,
  getStakeholderGroupById,
  getGrievanceById,
  calculateCommunityImpactTrends,
  getHighImpactPrograms,
  getCriticalStakeholders,
  getGrievancesByStatus,
  updateProgramProgress,
  updateStakeholderSatisfaction,
  addNewProgram,
  resolveGrievance,
  addGrievanceAction,
  generateCommunityEngagementReport,
  exportCommunityEngagementData,
  refreshCommunityEngagementData
} from '../../../services/esg/esgCommunityEngagementService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const ESGCommunityEngagement = () => {
  const [communityEngagementData, setCommunityEngagementData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({
    region: '',
    category: '',
    status: '',
    impactThreshold: ''
  });

  useEffect(() => {
    const unsubscribe = subscribe(setCommunityEngagementData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'esg_community_engagement', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = async () => {
    setLoading(true);
    await refreshCommunityEngagementData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'esg_community_engagement_data', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleProgramSelect = (program) => {
    setSelectedProgram(program);
    setActiveTab('program-detail');
  };

  const handleStakeholderSelect = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    setActiveTab('stakeholder-detail');
  };

  const handleGrievanceSelect = (grievance) => {
    setSelectedGrievance(grievance);
    setActiveTab('grievance-detail');
  };

  const handleExportReport = (programId = null, groupId = null, grievanceId = null) => {
    const reportData = generateCommunityEngagementReport(programId, groupId);
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `community_engagement_report_${programId || groupId || grievanceId || 'comprehensive'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBulkExport = () => {
    const exportData = exportCommunityEngagementData('json', filterCriteria);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `community_engagement_bulk_export_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading || !communityEngagementData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
            Loading ESG Community Engagement System...
          </p>
        </div>
      </div>
    );
  }

  const overview = getESGCommunityEngagementOverview();
  const programs = getPrograms();
  const stakeholderGroups = getStakeholderGroups();
  const impactMetrics = getImpactMetrics();
  const grievances = getGrievances();
  const benefitPrograms = getBenefitPrograms();
  const kpis = getKPIMonitoring();
  const complianceFramework = getComplianceFramework();
  const trends = calculateCommunityImpactTrends();
  const highImpactPrograms = getHighImpactPrograms();
  const criticalStakeholders = getCriticalStakeholders();
  const openGrievances = getGrievancesByStatus('In Progress');

  // Filter programs based on criteria
  const filteredPrograms = programs.filter(program => {
    if (filterCriteria.region && program.region !== filterCriteria.region) return false;
    if (filterCriteria.category && !program.category.toLowerCase().includes(filterCriteria.category.toLowerCase())) return false;
    if (filterCriteria.status && program.status !== filterCriteria.status) return false;
    if (filterCriteria.impactThreshold && program.impactScore < parseInt(filterCriteria.impactThreshold)) return false;
    return true;
  });

  const OverviewTab = () => (
    <div>
      {/* Key Metrics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* Community Programs Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(34, 197, 94, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)'
            }}>
              🎯
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Community Programs
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#22c55e',
              textShadow: '0 2px 8px rgba(34, 197, 94, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.totalPrograms}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>Total Programs</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#10b981', marginBottom: '4px' }}>
              {overview.activePrograms}
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Active Programs</div>
          </div>

          <div style={{
            height: '10px',
            background: 'rgba(34, 197, 94, 0.2)',
            borderRadius: '5px',
            overflow: 'hidden',
            marginBottom: '12px'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              width: `${(overview.activePrograms / overview.totalPrograms) * 100}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>

          <div style={{ fontSize: '14px', color: '#cbd5e1', textAlign: 'center', fontWeight: '600' }}>
            ${(overview.totalBudget / 1000000).toFixed(1)}M Total Budget
          </div>
        </div>

        {/* Social License Index Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(59, 130, 246, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
            }}>
              🛡️
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Social License to Operate
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#60a5fa',
              textShadow: '0 2px 8px rgba(59, 130, 246, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.socialLicenseIndex.toFixed(1)}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>License Index</div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Community Trust</span>
              <span style={{ fontSize: '13px', color: '#60a5fa', fontWeight: '700' }}>{overview.communityTrustScore.toFixed(1)}%</span>
            </div>
            <div style={{
              height: '6px',
              background: 'rgba(59, 130, 246, 0.2)',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                width: `${overview.communityTrustScore}%`,
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Avg Satisfaction</span>
              <span style={{ fontSize: '13px', color: '#22c55e', fontWeight: '700' }}>{overview.averageSatisfaction.toFixed(1)}%</span>
            </div>
            <div style={{
              height: '6px',
              background: 'rgba(34, 197, 94, 0.2)',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                width: `${overview.averageSatisfaction}%`,
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        </div>

        {/* Stakeholder Engagement Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(168, 85, 247, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4)'
            }}>
              👥
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Stakeholder Engagement
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#a855f7',
              textShadow: '0 2px 8px rgba(168, 85, 247, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.stakeholderGroups}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>Stakeholder Groups</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#c084fc', marginBottom: '4px' }}>
              {overview.totalStakeholders.toLocaleString()}
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Total Stakeholders</div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Participants</span>
              <span style={{ fontSize: '13px', color: '#a855f7', fontWeight: '700' }}>{overview.totalParticipants.toLocaleString()}</span>
            </div>
          </div>

          <div style={{
            height: '8px',
            background: 'rgba(168, 85, 247, 0.2)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #a855f7 0%, #c084fc 100%)',
              width: `${(overview.totalParticipants / overview.totalStakeholders) * 100}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>

        {/* Grievance Management Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
            }}>
              ⚖️
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Grievance Management
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#ef4444',
              textShadow: '0 2px 8px rgba(239, 68, 68, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.openGrievances}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>Open Grievances</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#22c55e', marginBottom: '4px' }}>
              {overview.resolvedGrievances}
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Resolved</div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Resolution Rate</span>
              <span style={{ fontSize: '13px', color: '#22c55e', fontWeight: '700' }}>
                {((overview.resolvedGrievances / (overview.openGrievances + overview.resolvedGrievances)) * 100).toFixed(1)}%
              </span>
            </div>
          </div>

          <div style={{
            height: '8px',
            background: 'rgba(34, 197, 94, 0.2)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              width: `${(overview.resolvedGrievances / (overview.openGrievances + overview.resolvedGrievances)) * 100}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>
      </div>

      {/* High Impact Programs and KPI Dashboard */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        {/* High Impact Programs */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(168, 85, 247, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              fontSize: '24px',
              background: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              ⭐
            </div>
            <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: 'white' }}>
              High Impact Community Programs
            </h3>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {highImpactPrograms.slice(0, 4).map((program, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 85, 247, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h6 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', color: 'white' }}>
                      {program.name}
                    </h6>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      {program.category} • {program.region}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '900', color: '#22c55e', textShadow: '0 2px 4px rgba(34, 197, 94, 0.4)' }}>
                      {program.impactScore.toFixed(1)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Impact</div>
                  </div>
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginRight: '8px' }}>Status:</span>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    background: program.status === 'active' ? 'rgba(34, 197, 94, 0.2)' :
                               program.status === 'completed' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                    color: program.status === 'active' ? '#22c55e' :
                          program.status === 'completed' ? '#60a5fa' : '#94a3b8'
                  }}>
                    {program.status.charAt(0).toUpperCase() + program.status.slice(1)}
                  </span>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '6px' }}>SDG Alignment:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {program.sdgAlignment.slice(0, 2).map((sdg, idx) => (
                      <span key={idx} style={{
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '10px',
                        fontWeight: '700',
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#60a5fa'
                      }}>
                        {sdg.split(':')[0]}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(168, 85, 247, 0.1)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#a855f7' }}>
                      {program.participantCount.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Participants</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#22c55e' }}>
                      ${(program.budget / 1000000).toFixed(1)}M
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Budget</div>
                  </div>
                </div>

                <button 
                  onClick={() => handleProgramSelect(program)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.02)';
                    e.target.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* KPI Dashboard */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(59, 130, 246, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              fontSize: '24px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              📈
            </div>
            <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: 'white' }}>
              KPI Monitoring
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {kpis.map((kpi, index) => {
              const percentage = (kpi.current / kpi.target) * 100;
              const isGood = kpi.current >= kpi.target * 0.9;
              const isMedium = kpi.current >= kpi.target * 0.8 && kpi.current < kpi.target * 0.9;
              
              return (
                <div key={index} style={{
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: `1px solid ${isGood ? 'rgba(34, 197, 94, 0.3)' : isMedium ? 'rgba(251, 191, 36, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  borderRadius: '10px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>{kpi.name}</span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '800',
                      background: isGood ? 'rgba(34, 197, 94, 0.2)' : isMedium ? 'rgba(251, 191, 36, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: isGood ? '#22c55e' : isMedium ? '#fbbf24' : '#ef4444'
                    }}>
                      {kpi.current.toFixed(1)}
                    </span>
                  </div>

                  <div style={{
                    height: '8px',
                    background: 'rgba(100, 116, 139, 0.2)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      height: '100%',
                      background: isGood 
                        ? 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)'
                        : isMedium 
                        ? 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'
                        : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                      width: `${Math.min(100, percentage)}%`,
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>
                      Target: {kpi.target}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: kpi.change.startsWith('+') ? '#22c55e' : '#ef4444'
                    }}>
                      {kpi.change}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const ProgramsTab = () => (
    <div className="row">
      {/* Filter Controls */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2"></i>Filter Community Programs
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <label className="form-label">Region</label>
                <select 
                  className="form-select"
                  value={filterCriteria.region}
                  onChange={(e) => setFilterCriteria({...filterCriteria, region: e.target.value})}
                >
                  <option value="">All Regions</option>
                  <option value="NA">North America</option>
                  <option value="EU">European Union</option>
                  <option value="APAC">Asia Pacific</option>
                  <option value="LATAM">Latin America</option>
                  <option value="MEA">Middle East & Africa</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Category</label>
                <input 
                  type="text"
                  className="form-control"
                  placeholder="Search categories..."
                  value={filterCriteria.category}
                  onChange={(e) => setFilterCriteria({...filterCriteria, category: e.target.value})}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Status</label>
                <select 
                  className="form-select"
                  value={filterCriteria.status}
                  onChange={(e) => setFilterCriteria({...filterCriteria, status: e.target.value})}
                >
                  <option value="">All Statuses</option>
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-end">
                  <button 
                    className="btn btn-outline-secondary me-2"
                    onClick={() => setFilterCriteria({ region: '', category: '', status: '', impactThreshold: '' })}
                  >
                    <i className="fas fa-times me-2"></i>Clear
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleBulkExport}
                  >
                    <i className="fas fa-download me-2"></i>Export
                  </button>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <span className="badge bg-info">
                  {filteredPrograms.length} programs found
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Programs Table */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-project-diagram me-2"></i>Community Engagement Programs
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Program</th>
                    <th>Category</th>
                    <th>Region</th>
                    <th>Status</th>
                    <th>Impact Score</th>
                    <th>Participants</th>
                    <th>Budget</th>
                    <th>Satisfaction</th>
                    <th>Timeline</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPrograms.map((program, index) => (
                    <tr key={index}>
                      <td>
                        <div className="fw-bold">{program.name}</div>
                        <small className="text-muted">{program.id}</small>
                      </td>
                      <td><span className="badge bg-secondary">{program.category}</span></td>
                      <td>{program.region}</td>
                      <td>
                        <span className={`badge ${
                          program.status === 'active' ? 'bg-success' :
                          program.status === 'completed' ? 'bg-info' :
                          program.status === 'planning' ? 'bg-warning' : 'bg-secondary'
                        }`}>
                          {program.status.charAt(0).toUpperCase() + program.status.slice(1)}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className={`badge me-2 ${
                            program.impactScore >= 90 ? 'bg-success' : 
                            program.impactScore >= 80 ? 'bg-info' :
                            program.impactScore >= 70 ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {program.impactScore.toFixed(1)}
                          </span>
                        </div>
                      </td>
                      <td>{program.participantCount.toLocaleString()}</td>
                      <td>${(program.budget / 1000000).toFixed(1)}M</td>
                      <td>
                        <span className={`badge ${
                          program.stakeholderSatisfaction >= 90 ? 'bg-success' : 
                          program.stakeholderSatisfaction >= 80 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {program.stakeholderSatisfaction.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        <small>
                          {new Date(program.startDate).toLocaleDateString()} - {new Date(program.endDate).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleProgramSelect(program)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleExportReport(program.id)}
                          title="Export Report"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const StakeholdersTab = () => (
    <div className="row">
      {/* Stakeholder Groups Cards */}
      <div className="col-12">
        <div className="row">
          {stakeholderGroups.map((group, index) => (
            <div key={index} className="col-md-6 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header">
                  <h5 className="mb-0">{group.name}</h5>
                  <small className="text-muted">{group.type} • {group.region}</small>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-4">
                      <div className="text-center p-2 border rounded">
                        <div className="h5 mb-0">{group.memberCount.toLocaleString()}</div>
                        <small className="text-muted">Members</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center p-2 border rounded">
                        <div className={`h5 mb-0 ${
                          group.satisfactionScore >= 90 ? 'text-success' :
                          group.satisfactionScore >= 80 ? 'text-warning' : 'text-danger'
                        }`}>
                          {group.satisfactionScore.toFixed(1)}%
                        </div>
                        <small className="text-muted">Satisfaction</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center p-2 border rounded">
                        <div className={`h6 mb-0 ${
                          group.influenceLevel === 'High' ? 'text-danger' :
                          group.influenceLevel === 'Medium' ? 'text-warning' : 'text-success'
                        }`}>
                          {group.influenceLevel}
                        </div>
                        <small className="text-muted">Influence</small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <strong className="small">Engagement Frequency:</strong>
                    <span className="badge bg-info ms-2">{group.engagementFrequency}</span>
                  </div>

                  <div className="mb-3">
                    <strong className="small">Communication Channels:</strong>
                    <div className="d-flex flex-wrap">
                      {group.communicationChannels.map((channel, idx) => (
                        <span key={idx} className="badge bg-secondary me-1 mb-1">
                          {channel}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong className="small">Key Concerns:</strong>
                    {group.concerns.slice(0, 3).map((concern, idx) => (
                      <div key={idx} className="d-flex justify-content-between align-items-center mt-1">
                        <small>{concern.issue}</small>
                        <span className={`badge ${
                          concern.severity === 'High' ? 'bg-danger' :
                          concern.severity === 'Medium' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {concern.severity}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Last: {new Date(group.lastEngagement).toLocaleDateString()}
                    </small>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleStakeholderSelect(group)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const GrievancesTab = () => (
    <div className="row">
      {/* Grievance Summary Cards */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-warning text-dark">
            <h6 className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>Open Grievances
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center">
              <div className="h2 text-warning">{openGrievances.length}</div>
              <small className="text-muted">Requiring Action</small>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-success text-white">
            <h6 className="mb-0">
              <i className="fas fa-check-circle me-2"></i>Resolved This Month
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center">
              <div className="h2 text-success">{grievances.filter(g => g.status === 'Resolved').length}</div>
              <small className="text-muted">Successfully Resolved</small>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-info text-white">
            <h6 className="mb-0">
              <i className="fas fa-clock me-2"></i>Avg Resolution Time
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center">
              <div className="h2 text-info">{impactMetrics.grievanceResolutionTime.toFixed(1)}</div>
              <small className="text-muted">Days</small>
            </div>
          </div>
        </div>
      </div>

      {/* Grievances Table */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-list me-2"></i>Grievance Management System
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Submitted By</th>
                    <th>Submitted Date</th>
                    <th>Assigned To</th>
                    <th>Expected Resolution</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grievances.map((grievance, index) => (
                    <tr key={index}>
                      <td>
                        <div className="fw-bold">{grievance.title}</div>
                        <small className="text-muted">{grievance.id}</small>
                      </td>
                      <td><span className="badge bg-secondary">{grievance.category}</span></td>
                      <td>
                        <span className={`badge ${
                          grievance.severity === 'High' ? 'bg-danger' :
                          grievance.severity === 'Medium' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {grievance.severity}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          grievance.status === 'Resolved' ? 'bg-success' :
                          grievance.status === 'In Progress' ? 'bg-warning' : 'bg-secondary'
                        }`}>
                          {grievance.status}
                        </span>
                      </td>
                      <td>{grievance.submittedBy}</td>
                      <td>{new Date(grievance.submittedDate).toLocaleDateString()}</td>
                      <td>{grievance.assignedTo}</td>
                      <td>{new Date(grievance.expectedResolution).toLocaleDateString()}</td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleGrievanceSelect(grievance)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleExportReport(null, null, grievance.id)}
                          title="Export Report"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ComplianceTab = () => (
    <div className="row">
      {/* Compliance Framework Cards */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-clipboard-check me-2"></i>GRI 413
            </h5>
            <small>Local Communities</small>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h2 text-primary">{complianceFramework.gri413.score.toFixed(1)}%</div>
              <small className="text-muted">Compliance Score</small>
            </div>
            {complianceFramework.gri413.requirements.map((req, index) => (
              <div key={index} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small className="fw-bold">{req.requirement.split(' ')[0]}</small>
                  <small>{req.compliance.toFixed(1)}%</small>
                </div>
                <div className="progress mt-1" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar ${
                      req.compliance >= 90 ? 'bg-success' : 
                      req.compliance >= 80 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${req.compliance}%` }}
                  ></div>
                </div>
              </div>
            ))}
            <div className="mt-3">
              <small className="text-muted">
                Next Assessment: {new Date(complianceFramework.gri413.nextAssessment).toLocaleDateString()}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-users me-2"></i>SASB Social Capital
            </h5>
            <small>Social Capital Management</small>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h2 text-success">{complianceFramework.sasbSocialCapital.score.toFixed(1)}%</div>
              <small className="text-muted">Compliance Score</small>
            </div>
            {complianceFramework.sasbSocialCapital.requirements.map((req, index) => (
              <div key={index} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small className="fw-bold">{req.requirement.split(' ')[0]}</small>
                  <small>{req.compliance.toFixed(1)}%</small>
                </div>
                <div className="progress mt-1" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar ${
                      req.compliance >= 90 ? 'bg-success' : 
                      req.compliance >= 80 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${req.compliance}%` }}
                  ></div>
                </div>
              </div>
            ))}
            <div className="mt-3">
              <small className="text-muted">
                Next Assessment: {new Date(complianceFramework.sasbSocialCapital.nextAssessment).toLocaleDateString()}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-handshake me-2" style={{ color: '#8b5cf6' }}></i>ISO 26000
            </h5>
            <small>Community Involvement</small>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h2 text-info">{complianceFramework.iso26000.score.toFixed(1)}%</div>
              <small className="text-muted">Compliance Score</small>
            </div>
            {complianceFramework.iso26000.requirements.map((req, index) => (
              <div key={index} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small className="fw-bold">{req.requirement.split(' ')[0]}</small>
                  <small>{req.compliance.toFixed(1)}%</small>
                </div>
                <div className="progress mt-1" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar ${
                      req.compliance >= 90 ? 'bg-success' : 
                      req.compliance >= 80 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${req.compliance}%` }}
                  ></div>
                </div>
              </div>
            ))}
            <div className="mt-3">
              <small className="text-muted">
                Next Assessment: {new Date(complianceFramework.iso26000.nextAssessment).toLocaleDateString()}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Benefit Programs */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-gift me-2"></i>Community Benefit Programs
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {benefitPrograms.map((program, index) => (
                <div key={index} className="col-md-4 mb-3">
                  <div className="card h-100 border">
                    <div className="card-body p-3">
                      <h6 className="card-title">{program.name}</h6>
                      <span className="badge bg-secondary mb-2">{program.type}</span>
                      
                      <div className="mb-2">
                        <div className="d-flex justify-content-between">
                          <small>Budget Utilization</small>
                          <small>{((program.spentAmount / program.totalBudget) * 100).toFixed(1)}%</small>
                        </div>
                        <div className="progress mt-1" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar bg-info"
                            style={{ width: `${(program.spentAmount / program.totalBudget) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="fw-bold">Beneficiaries: </small>
                        <span className="text-success">{program.beneficiaryCount.toLocaleString()}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="fw-bold">Focus Areas:</small>
                        <div className="d-flex flex-wrap">
                          {program.focusAreas.slice(0, 2).map((area, idx) => (
                            <span key={idx} className="badge bg-primary me-1 mb-1 small">
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <small className="text-muted">
                        Next Review: {new Date(program.nextReview).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* SVG Pattern Overlay */}
        <svg style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.1
        }}>
          <pattern id="community-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 0 30 L 30 30 M 30 0 L 30 60" stroke="white" strokeWidth="1" fill="none"/>
          </pattern>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#community-pattern)"/>
        </svg>

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}>
                <HandshakeIcon size={24} />
              </div>
              <div>
                <h1 style={{
                  margin: 0,
                  fontSize: '42px',
                  fontWeight: '900',
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.5px'
                }}>
                  ESG Community Engagement
                </h1>
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '18px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: '500'
                }}>
                  Stakeholder relations, social license to operate, and community development
                </p>
              </div>
            </div>
            
            <button 
              onClick={handleRefreshData}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <i className="fas fa-sync-alt"></i> Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(168, 85, 247, 0.3)',
        padding: '20px 40px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { id: 'overview', label: '📊 Overview' },
              { id: 'programs', label: '🎯 Programs' },
              { id: 'stakeholders', label: '👥 Stakeholders' },
              { id: 'grievances', label: '⚖️ Grievances' },
              { id: 'compliance', label: '🛡️ Compliance' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 24px',
                  background: activeTab === tab.id 
                    ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
                    : 'transparent',
                  border: activeTab === tab.id 
                    ? '2px solid rgba(168, 85, 247, 0.5)'
                    : '2px solid transparent',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: activeTab === tab.id 
                    ? '0 4px 12px rgba(124, 58, 237, 0.4)'
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'rgba(168, 85, 247, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'programs' && <ProgramsTab />}
        {activeTab === 'stakeholders' && <StakeholdersTab />}
        {activeTab === 'grievances' && <GrievancesTab />}
        {activeTab === 'compliance' && <ComplianceTab />}
      </div>
    </div>
  );
};

export default ESGCommunityEngagement;